import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import Decimal from 'decimal.js-light';
import { ReactNode, useEffect, useState, type ChangeEvent } from 'react';

import {
  DEFAULT_TOKEN_INPUT,
  type InputMode,
  type TokenInputValues,
} from '@/components/TransactionModule/store';
import type { TokenUsdPrices } from '@/components/TransactionModule/tradeQuote';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { tokenFormatter } from '../../../lib/formatting';
import { inputStyles } from '../../../styles/input';
import { fontStyles } from '../../../styles/typography';
import { isNullAmount } from '../../../utils/numbers';
import type { TradeDirection } from '../types';
import { UsdInput } from '../UsdInput';

type InputVisualStyle = 'default' | 'locked';

/** One shimmer sweep duration. Keep in sync with the `shimmer-sweep` CSS animation. */
const SHIMMER_DURATION_MS = 1200;

/**
 * Props for the `AmountInput` component.
 */
interface AmountInputGroupProps {
  /** Buy input values. */
  buyInput: TokenInputValues;
  /** Maximum number of decimals allowed */
  decimals?: number;
  /** Direction of the input. */
  direction: TradeDirection;
  /** Whether the input is disabled. */
  disabled?: boolean;
  /** Shimmer the displayed amount once each time it changes to a new value (opt-in; use on read-only quoted fields). */
  shimmerOnUpdate?: boolean;
  /** Callback function invoked at the end of `handleChange` event handler. */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Sell input values. */
  sellInput: TokenInputValues;
  /** Token symbol. */
  symbol?: string;
  /** Token USD prices. */
  tokenUsdPrices: TokenUsdPrices;
  /** Whether the sell amount is locked. */
  isSellAmountLocked?: boolean;
  /** The input visual style */
  inputVisualStyle?: InputVisualStyle;
  /** Updates the buy input in the store. */
  updateBuyInput: (buyInput?: TokenInputValues, direction?: TradeDirection) => void;
  /** Updates the sell input in the store. */
  updateSellInput: (sellInput?: TokenInputValues, direction?: TradeDirection) => void;
  /** Updates both inputs in the store. */
  updateInputs: (
    buyInput?: TokenInputValues,
    sellInput?: TokenInputValues,
    direction?: TradeDirection,
  ) => void;
  /** Sets the input mode of the swap form */
  setInputMode: (mode: InputMode) => void;
  /** Sets the sell amount lock status */
  setIsSellAmountLocked?: (isSellAmountLocked: boolean) => void;
  /** Children of the component */
  children?: ReactNode;
}

/**
 * A text input for the amount of tokens to buy or sell.
 *
 * @param props - Component properties.
 */
export const AmountInputGroup = ({
  buyInput,
  decimals,
  direction,
  disabled,
  shimmerOnUpdate = false,
  onChange,
  sellInput,
  symbol,
  tokenUsdPrices,
  isSellAmountLocked = false,
  inputVisualStyle = 'default',
  children,
  updateBuyInput,
  updateSellInput,
  updateInputs,
  setInputMode,
  setIsSellAmountLocked,
}: AmountInputGroupProps): React.ReactElement => {
  // state
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isUpdateShimmering, setIsUpdateShimmering] = useState<boolean>(false);
  const { fxRate } = useFiatDisplay();

  // event handlers
  const handleBlur = (): void => setIsFocused(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInputMode('tokenAmount');

    // validate input
    const {
      target: { validity, value: inputValue },
    } = event;
    const value = inputValue.replace(',', '.');
    const exceedsDecimals = decimals && value.split('.')[1]?.length > decimals;
    if (!validity.valid) return;

    // if we exceed decimals, we trim, otherwise we use the current value
    const verified = exceedsDecimals
      ? value.substring(0, value.includes('.') ? decimals + 1 : decimals)
      : value;

    // sanitize & parse amount
    const numericAmount = verified !== '.' ? parseFloat(verified) : 0;
    if (
      isNullAmount(numericAmount) &&
      numericAmount !== 0 &&
      (direction === 'sell' || !isSellAmountLocked)
    ) {
      resetInputs();
      return;
    }

    updateSwapInputs(value, numericAmount);
    onChange?.(event);
  };

  const handleUsdInputChange = (fiatValue: Decimal) => {
    const { sellTokenUsd, buyTokenUsd } = tokenUsdPrices;
    setInputMode('usdPrice');

    // Convert the user's fiat input back to USD for internal storage
    const usdValue = fxRate === 1 ? fiatValue : fiatValue.div(fxRate);

    const [tokenPrice, updateInput] =
      direction === 'sell' ? [sellTokenUsd, updateSellInput] : [buyTokenUsd, updateBuyInput];

    if (usdValue.eq(0) || !tokenPrice) {
      updateBuyInput(DEFAULT_TOKEN_INPUT, direction);
      updateSellInput(DEFAULT_TOKEN_INPUT, direction);
    } else {
      const newAmount = usdValue.div(tokenPrice).toString();
      const newAmountInput = {
        decimal: newAmount,
        formatted: newAmount,
        usd: usdValue.toNumber(),
      };
      updateInput(newAmountInput, direction);
    }
  };

  const handleFocus = (): void => setIsFocused(true);

  const resetInputs = (): void => {
    updateInputs(DEFAULT_TOKEN_INPUT, DEFAULT_TOKEN_INPUT, direction);
    setIsSellAmountLocked?.(false);
  };

  const { input: inputStyle } = inputStyles();

  const updateSwapInputs = (value: string, numericAmount: number): void => {
    const { buyTokenUsd, sellTokenUsd } = tokenUsdPrices;

    const [updateInputFunction, updateOtherInputFunction] =
      direction === 'buy' ? [updateBuyInput, updateSellInput] : [updateSellInput, updateBuyInput];

    const newInputUsd = (direction === 'buy' ? buyTokenUsd : sellTokenUsd) * numericAmount;
    const newInputFormatted = isNullAmount(parseFloat(value))
      ? ''
      : tokenFormatter.format(value, { mode: 'write' });

    const newInput = { usd: newInputUsd, decimal: value, formatted: newInputFormatted };
    updateInputFunction(newInput, direction);

    if (numericAmount === 0) {
      updateOtherInputFunction(DEFAULT_TOKEN_INPUT, direction);
    }
  };

  // variables
  const input = direction === 'buy' ? buyInput : sellInput;
  const amount = isFocused ? input.decimal : input.formatted;
  const inputId = `${direction}Amount`;
  const label = `${direction.charAt(0).toUpperCase()}${direction.slice(1)} Amount`;

  // Play a one-shot shimmer sweep whenever the displayed amount changes to a new
  // value (e.g. a freshly quoted amount). The effect only re-runs when `amount`
  // actually changes, so reaching here means it's a new value; it fires after the
  // update lands, never while the value is stale or being fetched. The cleanup
  // cancels a still-running sweep before the next one and on unmount.
  useEffect(() => {
    if (!shimmerOnUpdate || amount === '') return;

    setIsUpdateShimmering(true);
    const timer = setTimeout(() => setIsUpdateShimmering(false), SHIMMER_DURATION_MS);
    return () => clearTimeout(timer);
  }, [amount, shimmerOnUpdate]);

  const showShimmer = shimmerOnUpdate && isUpdateShimmering && amount !== '';

  // render
  return (
    <div className="flex justify-between items-end mt-4 mb-2 h-fit">
      <VisuallyHidden.Root>
        <label htmlFor={inputId}>{label}</label>
      </VisuallyHidden.Root>

      {showShimmer ? (
        <span
          data-testid={`${direction}-amount-shimmer`}
          className={fontStyles({
            variant: 'H3',
            class: [
              'w-full',
              'max-w-3/4',
              'pr-4',
              'h-9',
              'leading-9',
              'inline-block',
              'overflow-hidden',
              'text-ellipsis',
              'whitespace-nowrap',
              'bg-token-gray-1600',
              'shimmer-sweep',
              'highlight-token-gray-700',
              'motion-reduce:animate-none',
            ],
          })}
        >
          {amount}
        </span>
      ) : (
        <input
          autoComplete="off"
          autoCorrect="off"
          className={inputStyle({
            class: [
              'w-full',
              'pr-4',
              'h-9',
              'bg-transparent',
              'border-none',
              'hover:outline-none',
              'focus:outline-none',
              'placeholder:text-token-gray-600',
              'disabled:cursor-not-allowed',
              'disabled:bg-transparent',
              'disabled:text-token-gray-1600',
              'text-inherit',
              'data-[visual-style=locked]:text-token-gray-600',
              fontStyles({ variant: 'H3' }),
              'max-w-3/4',
              'overflow-hidden',
              'text-ellipsis',
              'whitespace-nowrap',
              'py-0',
            ],
          })}
          disabled={disabled}
          id={inputId}
          inputMode="decimal"
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          pattern="^\d*([.,]\d*)?$"
          placeholder="0.0"
          spellCheck="false"
          title={`Amount of ${symbol ?? 'tokens'} to ${direction}`}
          type="text"
          value={amount}
          data-visual-style={inputVisualStyle}
        ></input>
      )}
      {!disabled && children == null && (
        <UsdInput.Root className="text-token-gray-700">
          <UsdInput.Input
            onChange={handleUsdInputChange}
            currentValue={new Decimal(input.usd * fxRate)}
            className="text-token-gray-700"
          />
        </UsdInput.Root>
      )}
      {children}
    </div>
  );
};
